import {
  Button,
  ButtonColor,
  ButtonWidth,
  IconArrowRight,
  ToastDuration,
  ToastType,
  useToast,
} from '@leland-dev/leland-ui-library';
import { type FC, useCallback, useEffect, useState } from 'react';

import {
  MatchedCoachStatus,
  type Scalars,
} from '../../../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../../../context/AuthContext';
import { wasMutationSuccessful } from '../../../../utils/mutations';
import RadioInput from '../../../inputs/RadioInput';
import { AssignCoachingCard } from '../../../subscriber/AssignCoachingCard';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';
import {
  type AssignExistingOrderOrderFragment,
  useAssignExistingOrderCreateOpportunityMutation,
} from './__generated-gql-types__/AssignExistingOrder.generated';

interface AssignExistingOrderProps
  extends BaseScheduleSubmissionModalLayoutProps {
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  coachUrn: Scalars['CoachUrn']['output'];
  hasRelationship: boolean;
  unassignedOrders: AssignExistingOrderOrderFragment[];
  unassignedOrderSelection: Nullable<AssignExistingOrderOrderFragment>;
  setUnassignedOrderSelection: (
    unasisgnedOrderSelection: Nullable<AssignExistingOrderOrderFragment>,
  ) => void;
  onMoveToSubmissionStep: (isComingFromCategorySelection?: boolean) => void;
}

export const AssignExistingOrder: FC<AssignExistingOrderProps> = ({
  unassignedOrders,
  hasUnlimitedBookingPower,
  coach,
  order,
  selectedTime,
  sessionLength,
  setOpen,
  setSelectedTime,
  onBackClick,
  sessionPrice,
  unassignedOrderSelection,
  setUnassignedOrderSelection,
  onMoveToSubmissionStep,
  categoryUrn,
  coachUrn,
  hasRelationship,
  onBackToStart,
}) => {
  const [shouldMoveToSubmissionStep, setShouldMoveToSubmissionStep] =
    useState(false);

  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [createOpportunityAction] =
    useAssignExistingOrderCreateOpportunityMutation();

  const onAssignOrderClick = useCallback(async () => {
    if (hasRelationship) {
      return onMoveToSubmissionStep();
    }

    if (
      currentUser?.applicant?.id &&
      unassignedOrderSelection?.id &&
      categoryUrn
    ) {
      const res = await createOpportunityAction({
        variables: {
          matchedCoachInput: {
            applicantUrn: currentUser.applicant.id,
            coachUrn: coachUrn,
            opportunityOrderUrn: unassignedOrderSelection.id,
            categoryUrn,
            status: MatchedCoachStatus.REQUESTED,
          },
        },
      });
      if (wasMutationSuccessful(res)) {
        onMoveToSubmissionStep();
        showToast({
          message: 'Your request has been sent!',
          type: ToastType.SUCCESS,
          duration: ToastDuration.NORMAL,
        });
      } else {
        showToast({
          message: 'Something went wrong. Please try again.',
          type: ToastType.ERROR,
          duration: ToastDuration.NORMAL,
        });
      }
    }
  }, [
    hasRelationship,
    categoryUrn,
    coachUrn,
    currentUser?.applicant?.id,
    createOpportunityAction,
    onMoveToSubmissionStep,
    showToast,
    unassignedOrderSelection?.id,
  ]);

  const handleBuyCoachingTime = () => {
    setUnassignedOrderSelection(null);
    setShouldMoveToSubmissionStep(true);
  };

  useEffect(() => {
    if (unassignedOrderSelection === null && shouldMoveToSubmissionStep) {
      onMoveToSubmissionStep();
      setShouldMoveToSubmissionStep(false);
    }
  }, [
    unassignedOrderSelection,
    shouldMoveToSubmissionStep,
    onMoveToSubmissionStep,
  ]);

  return (
    <BaseScheduleSubmissionModalLayout
      buttonProps={{
        onClick: () => void onAssignOrderClick(),
        label: 'Assign Order',
        disabled: !unassignedOrderSelection,
      }}
      customSidebar={{
        title: 'Assign an existing order?',
        subtitle: `You don’t have enough time on your balance to cover this session. To continue with this session, you can assign an existing order or purchase more coaching time from ${coach.user.firstName}.`,
      }}
      coach={coach}
      hasUnlimitedBookingPower={hasUnlimitedBookingPower}
      order={order}
      selectedTime={selectedTime}
      sessionLength={sessionLength}
      setOpen={setOpen}
      setSelectedTime={setSelectedTime}
      onBackClick={onBackClick}
      sessionPrice={sessionPrice}
      onBackToStart={onBackToStart}
    >
      <div className="flex w-full grow flex-col overflow-auto overscroll-contain p-6 md:pb-40">
        <div className="flex flex-col gap-4">
          <p className="text-lg font-medium text-leland-gray-dark">
            Select an order:
          </p>
          {unassignedOrders.map((unassignedOrder) => {
            return (
              <div key={unassignedOrder.id} className="border pl-4">
                <RadioInput
                  name="unassigned-order"
                  option={{
                    value: unassignedOrder.id,
                    label: (
                      <AssignCoachingCard order={unassignedOrder} compact />
                    ),
                  }}
                  checked={unassignedOrder.id === unassignedOrderSelection?.id}
                  onChange={() => {
                    setUnassignedOrderSelection(unassignedOrder);
                  }}
                  onClick={() => {
                    if (unassignedOrder.id === unassignedOrderSelection?.id) {
                      setUnassignedOrderSelection(null);
                    }
                  }}
                />
              </div>
            );
          })}
          <Button
            label="Buy coaching time"
            RightIcon={IconArrowRight}
            buttonColor={ButtonColor.GRAY}
            width={ButtonWidth.FULL}
            onClick={handleBuyCoachingTime}
          />
        </div>
      </div>
    </BaseScheduleSubmissionModalLayout>
  );
};
